import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import venderService from "@/service/vender.service";
import ToastService from "@/service/toaster.service";
import LoginPopup from "@/common/LoginPopup";

export default function ScheduleCallSection({ VendorDetail }) {
  const isAuthenticated = Boolean(localStorage.getItem("access_token"))
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [dateError, setDateError] = useState("");
  const [timeError, setTimeError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const customerId = useSelector((s) => s.auth.auth.id);
  const vendorId = VendorDetail._id;

  const today = new Date().toISOString().split("T")[0];
  const maxDate = new Date(
    new Date().getFullYear(),
    11,
    31
  ).toISOString().split("T")[0];

  const getCurrentTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  };

  const handleSubmit = async () => {

    if (!isAuthenticated) {
      setRedirectUrl("/customer/auth/login");
      setShowPopup(true);
      return;
    }
    let valid = true;
    if (!date) {
      setDateError("Please select a date.");
      valid = false;
    }
    if (!time) {
      setTimeError("Please select a time.");
      valid = false;
    }
    if (!valid) return;

    if (date === today && time < getCurrentTime()) {
      setTimeError("You cannot select a past time.");
      return;
    }

    try {
      const localDateTime = new Date(`${date}T${time}`);
      const formatted = `${localDateTime
        .getFullYear()}-${String(localDateTime.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(localDateTime.getDate()).padStart(2, "0")} ${String(
          localDateTime.getHours()
        ).padStart(2, "0")}:${String(localDateTime.getMinutes()).padStart(
          2,
          "0"
        )}:${String(localDateTime.getSeconds()).padStart(2, "0")}`;

      await venderService.scheduleTimeSlot(
        customerId,
        vendorId,
        formatted
      );
      ToastService.success("Call scheduled successfully");
      setDate("");
      setTime("");
    } catch {
      ToastService.error("Something went wrong while scheduling. Try again!");
    }
  };

  const theme = useTheme();
  const isNarrow = useMediaQuery("(max-width:991px)");

  return (
    <>
      <Box
        component="section"
        sx={{
          mt: 1,
          bgcolor: "#fbfcff",
          border: "1px solid #d9d9d9",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
          p: isNarrow ? 2 : "16px 60px 16px 38px",
          fontFamily: `"Poppins", ${theme.typography.fontFamily}`,
        }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Choose a Time for the Expert to Call You
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#6e6d6a",
            fontSize: 16,
            fontWeight: 400,
            mb: 1,
          }}
        >
          Select your preferred time slot, and the expert will reach out to you
          accordingly.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            width: "100%",
            justifyContent: "space-between", // Ensure space between elements
            alignItems: "center",  // <--- changed here
          }}
        >
          {/* Date Picker */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
              Date <Typography component="span" sx={{ color: "grey", fontSize: "0.75rem" }}>dd-mm-yyyy</Typography>
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={date}
              onChange={(e) => {
                const v = e.target.value;
                setDate(v);
                if (v < today) setDateError("You cannot select a past date.");
                else setDateError("");
                setTime("");
              }}
              inputProps={{ min: today, max: maxDate }}
              error={!!dateError}
              helperText={dateError}
            />
          </Box>

          {/* Time Picker */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
              Time <Typography component="span" sx={{ color: "grey", fontSize: "0.75rem" }}>HH:MM AM/PM</Typography>
            </Typography>
            <TextField
              fullWidth
              type="time"
              size="small"
              value={time}
              onChange={(e) => {
                const v = e.target.value;
                setTime(v);
                if (date === today && v < getCurrentTime())
                  setTimeError("You cannot select a past time.");
                else setTimeError("");
              }}
              error={!!timeError}
              helperText={timeError}
            />
          </Box>

          {/* Submit Button */}
          <Box sx={{ flex: "none", mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#5e5acf",
                color: "#fff",
                borderRadius: 1,
                textTransform: "uppercase",
                fontWeight: 600,
                py: 0.75,
                px: isNarrow ? 2 : 4,
                fontSize: { xs: "14px", sm: "16px" }, // responsive font
                lineHeight: 1,
                minHeight: 40,
                "&:hover": { backgroundColor: "#4a49b6" },
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
      {showPopup && (
        <LoginPopup
          onClose={() => setShowPopup(false)}
          redirectUrl={redirectUrl}
        />
      )}</>
  );
}
